import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import type { DashboardSummary } from '@foundation/shared';
import { api } from '../lib/api';
import { formatNumber, formatSatang } from '../lib/format';
import { ErrorAlert, Loading } from '../components/ui';
import { useAuth } from '../auth';

function Stat({
  label,
  value,
  sub,
  tone,
}: {
  label: string;
  value: string;
  sub?: string;
  tone?: 'warn' | 'danger';
}) {
  return (
    <div className="card">
      <div className="stat-label">{label}</div>
      <div className={`stat-value${tone ? ` ${tone}` : ''}`}>{value}</div>
      {sub && <div className="stat-sub">{sub}</div>}
    </div>
  );
}

export default function Dashboard() {
  const { me } = useAuth();
  const [data, setData] = useState<DashboardSummary | null>(null);
  const [error, setError] = useState<unknown>(null);

  useEffect(() => {
    const ac = new AbortController();
    api<DashboardSummary>('/reports/dashboard', { signal: ac.signal })
      .then(setData)
      .catch((e) => {
        if (!ac.signal.aborted) setError(e);
      });
    return () => ac.abort();
  }, []);

  if (error) return <ErrorAlert error={error} />;
  if (!data) return <Loading />;

  return (
    <>
      <div className="page-head">
        <div>
          <h1>ภาพรวม</h1>
          <p>สวัสดีคุณ{me?.nickname ?? me?.fullName} — ตัวเลขทั้งหมดอยู่ในขอบเขตข้อมูลที่คุณดูแล</p>
        </div>
      </div>

      <div className="grid grid-4">
        <Stat
          label="ผู้รับความช่วยเหลือทั้งหมด"
          value={formatNumber(data.beneficiaries.total)}
          sub={`กำลังช่วยเหลือ ${formatNumber(data.beneficiaries.active)} · ติดตามผล ${formatNumber(data.beneficiaries.monitoring)}`}
        />
        <Stat
          label="รอคัดกรอง"
          value={formatNumber(data.beneficiaries.screening)}
          sub="ยังไม่ได้ประเมินความเดือดร้อน"
          tone={data.beneficiaries.screening > 0 ? 'warn' : undefined}
        />
        <Stat
          label="เจ้าหน้าที่ปฏิบัติงาน"
          value={formatNumber(data.staff.active)}
          sub={
            data.staff.unlinked > 0
              ? `รอผูกบัญชี LINE ${formatNumber(data.staff.unlinked)} คน`
              : 'ผูกบัญชี LINE ครบทุกคน'
          }
          tone={data.staff.unlinked > 0 ? 'warn' : undefined}
        />
        <Stat
          label="เยี่ยมเลยกำหนด"
          value={formatNumber(data.visits.overdue)}
          sub={`นัดวันนี้ ${formatNumber(data.visits.scheduledToday)} รายการ`}
          tone={data.visits.overdue > 0 ? 'danger' : undefined}
        />
      </div>

      <div className="grid grid-2" style={{ marginTop: 16 }}>
        <div className="card">
          <h2>งานภาคสนาม</h2>
          <dl className="detail-list">
            <dt>นัดเยี่ยมวันนี้</dt>
            <dd>{formatNumber(data.visits.scheduledToday)} รายการ</dd>
            <dt>เลยกำหนด</dt>
            <dd>{formatNumber(data.visits.overdue)} รายการ</dd>
            <dt>เสร็จสิ้นเดือนนี้</dt>
            <dd>{formatNumber(data.visits.completedThisMonth)} รายการ</dd>
          </dl>
          <p className="muted small" style={{ marginBottom: 0, marginTop: 12 }}>
            แบบสำรวจและการเยี่ยมติดตามจะเปิดใช้ในเฟส 2
          </p>
        </div>

        <div className="card">
          <h2>เคสและเงินบริจาค</h2>
          <dl className="detail-list">
            <dt>เคสที่เปิดอยู่</dt>
            <dd>{formatNumber(data.cases.open)} เคส</dd>
            <dt>รออนุมัติ</dt>
            <dd>{formatNumber(data.cases.pendingApproval)} เคส</dd>
            <dt>สลิปรอตรวจสอบ</dt>
            <dd>{formatNumber(data.donations.pendingVerify)} รายการ</dd>
            <dt>ยอดบริจาคเดือนนี้</dt>
            <dd>{formatSatang(data.donations.verifiedThisMonthSatang)}</dd>
          </dl>
          <p className="muted small" style={{ marginBottom: 0, marginTop: 12 }}>
            เคสความช่วยเหลือเปิดใช้ในเฟส 4 · ระบบบริจาคเปิดใช้ในเฟส 3
          </p>
        </div>
      </div>

      <div className="card" style={{ marginTop: 16 }}>
        <h2>เริ่มต้นใช้งาน</h2>
        <ol style={{ margin: 0, paddingInlineStart: 20 }} className="small">
          <li>
            เพิ่มเจ้าหน้าที่ที่หน้า <Link to="/staff">จัดการเจ้าหน้าที่</Link> แล้วกด
            &ldquo;สร้างลิงก์ผูกบัญชี&rdquo; ส่งลิงก์ให้เจ้าหน้าที่เปิดใน LINE
          </li>
          <li>กำหนดพื้นที่รับผิดชอบให้เจ้าหน้าที่ภาคสนาม — ระบบใช้กำหนดขอบเขตข้อมูลที่เห็น</li>
          <li>
            บันทึกผู้รับความช่วยเหลือที่หน้า{' '}
            <Link to="/beneficiaries">ผู้รับความช่วยเหลือ</Link> โดยกรอกเลขบัตรประชาชนเพื่อกันบันทึกซ้ำ
          </li>
        </ol>
      </div>
    </>
  );
}
